import { memo, useCallback, useState } from 'react';

import { merge } from '@/components';
import {
  Button,
  Heading,
  Input,
  Label,
  ListBox,
  ListBoxItem,
  Popover,
  Select,
  SelectValue,
  Text,
} from '@/components/core';
import { CheckmarkIcon, ChevronIcon, SaveIcon } from '@/components/icons';
import { useGetAgentFromPath } from '@/hooks';
import { toOrganizationId, useGetOrganization } from '@/hooks/api';
import { Notification, useValidationConfigStore } from '@/store';
import { DivProps, generateId } from '@/utils';

import { Cron, FrequencySetting } from './FrequencySetting';

const HTTP_METHODS = ['POST', 'GET'];

export type HttpNotificationConfigurationProps = DivProps & {
  id: string;
};

const IHttpNotificationConfiguration = ({ className, id, ...rest }: HttpNotificationConfigurationProps) => {
  const updateNotification = useValidationConfigStore((state) => state.updateNotification);
  const notification = useValidationConfigStore((state) => state.getNotification(id));

  const updateHttpSettings = useCallback(
    ({ httpMethod, url }: Pick<Notification, 'httpMethod' | 'url'>) => {
      updateNotification(id, (notification) => {
        const changes = {} as Partial<Notification>;
        if (httpMethod) changes.httpMethod = httpMethod;
        if (url) changes.url = url;
        /* Add default when and timezone settings if not present */
        if (notification.when == null) changes.when = new Cron().cronExpression();
        if (notification.timezone == null) changes.timezone = new Cron().timezone;
        return { ...notification, ...changes };
      });
    },
    [id, updateNotification]
  );

  const [isTestingWebhook, setIsTestingWebhook] = useState(false);
  const [isDoneTestingWebhook, setIsDoneTestingWebhook] = useState(false);
  const { data: agent } = useGetAgentFromPath();
  const { data: organization } = useGetOrganization(toOrganizationId('me'));

  const testWebhook = useCallback(() => {
    if (notification?.url && notification?.httpMethod && agent && organization) {
      setIsTestingWebhook(true);
      setIsDoneTestingWebhook(false);

      const body = {
        organization: {
          id: organization.organizationId,
          name: organization.name,
        },
        agent: {
          id: agent.agentId,
          name: agent.name,
        },
        tasks: [
          {
            id: `cradl:validation-task:${generateId()}`,
            url: `${import.meta.env.VITE_APP_URL}/details/agents/${agent.agentId}/runs/${generateId()}?showPanel=agent-runs`,
          },
          {
            id: `cradl:validation-task:${generateId()}`,
            url: `${import.meta.env.VITE_APP_URL}/details/agents/${agent.agentId}/runs/${generateId()}?showPanel=agent-runs`,
          },
          {
            id: `cradl:validation-task:${generateId()}`,
            url: `${import.meta.env.VITE_APP_URL}/details/agents/${agent.agentId}/runs/${generateId()}?showPanel=agent-runs`,
          },
        ],
        numberOfTasks: 3,
        status: 'ready',
        url: `${import.meta.env.VITE_APP_URL}/agents/${agent.agentId}/inbox`,
      };

      fetch(notification.url, {
        method: notification.httpMethod.toUpperCase(),
        body: JSON.stringify(body),
        mode: 'no-cors',
      }).finally(() => {
        setIsTestingWebhook(false);
        setIsDoneTestingWebhook(true);

        setTimeout(() => {
          setIsDoneTestingWebhook(false);
        }, 2000);
      });
    }
  }, [agent, notification, organization]);

  return (
    <div className={merge('flex flex-col gap-4 p-4', className)} {...rest}>
      <div className="flex flex-col">
        <Heading size="xs">Webhook</Heading>
        <Text>Send HTTP request when a document needs review.</Text>
      </div>

      <div className="flex flex-col gap-2">
        <Label>URL</Label>

        <div className="flex gap-2">
          {/* URL */}
          <Input value={notification?.url} onChange={(e) => updateHttpSettings({ url: e.target.value })} />

          {/* HTTP Method */}
          <Select
            className="w-fit"
            selectedKey={notification?.httpMethod ?? 'POST'}
            onSelectionChange={(httpMethod) => updateHttpSettings({ httpMethod: httpMethod?.toString() })}
          >
            <div>
              <Button variant="secondary">
                <SelectValue />
                <ChevronIcon rotation="down" />
              </Button>
            </div>

            <Popover>
              <ListBox className="max-h-64 overflow-y-auto">
                {HTTP_METHODS.map((httpMethod) => (
                  <ListBoxItem key={httpMethod} id={httpMethod}>
                    <Text>{httpMethod}</Text>
                  </ListBoxItem>
                ))}
              </ListBox>
            </Popover>
          </Select>
        </div>
      </div>

      <Button variant="secondary" onPress={testWebhook} isDisabled={isTestingWebhook || !notification?.url}>
        {isTestingWebhook ? <SaveIcon /> : null}
        <Text>Test webhook</Text>
        {isDoneTestingWebhook ? <CheckmarkIcon className="animate-fade" /> : null}
      </Button>

      <FrequencySetting id={id} />
    </div>
  );
};

export const HttpNotificationConfiguration = memo(
  IHttpNotificationConfiguration
) as typeof IHttpNotificationConfiguration;
